import React from 'react';
import {
  Box,
  Grid,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { HtmlRenderer } from './HtmlRenderer';
import { AppColors, typographyClamps } from '../utils/utils';

interface SectionViewProps {
  idx: number;
  label: string;
  title?: string;
  subtitle?: string;
  features: string[];
  footerText: string;
  imagePath: string;
  imageBGPath?: string;
  imageAlign?: 'start' | 'center' | 'end';
  isForFeature?: boolean;
  titleTextColor?: string;
  transparentBackground?: boolean;
}

export const BenefitSectionView: React.FC<SectionViewProps> = ({
  idx,
  label,
  title,
  subtitle,
  features,
  footerText,
  imagePath,
  imageBGPath,
  imageAlign,
  isForFeature,
  titleTextColor,
  transparentBackground,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // e.g. <600px

  const isImageOnRight = idx % 2 !== 0;

  const gridSize = isForFeature
    ? { xs: 12, sm: 5.5, md: 5.5 }
    : { xs: 5.5, sm: 5.5, md: 5.5 };

  const getScreenshotView = () => (
    <Box
      sx={{
        display: 'flex',
        justifyContent: imageAlign ?? 'center',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {imageBGPath && (
        <Box
          component='img'
          src={imageBGPath}
          sx={{
            position: 'absolute',
            display: 'block',
            right: {
              sm: 0,
              md: isImageOnRight ? (imageAlign === 'end' ? '-60%' : '-30%') : 0,
            },
            left: { sm: 0, md: isImageOnRight ? 0 : '-30%' },
            height: isMobile ? '100%' : '100%',
            width: isMobile ? '100%' : 'auto',
            // rotate: idx === 1 ? '30deg' : idx === 2 ? '-150deg' : '0deg',
          }}
        />
      )}

      <Box
        component='img'
        src={imagePath}
        width={{
          xs: isForFeature ? '80%' : '100%',
          sm: '100%',
          md: '80%',
          lg: '70%',
        }}
        height='auto'
        // maxHeight={'270px'}
        // maxWidth={'270px'}
        sx={{ position: 'relative' }}
      />

      {/* <Typography
        sx={{
          color: 'white',
          fontSize: 'clamp(1.125rem, 3vw, 2.125rem)',
          fontWeight: 'bold',
          position: 'absolute',
          bottom: 5,
          left: idx === 0 || !isImageOnRight ? undefined : '50%',
          right: idx === 0 || isImageOnRight ? undefined : '50%',
        }}
      >
        {label}
      </Typography> */}
    </Box>
  );

  const getFeatureListView = (list: string[]) => {
    
    return (
      <Box
        display='flex'
        flexDirection='column'
        justifyContent='center'
        alignItems='start'
      >
        {list.map((item, index) => (
          <Stack
            key={index}
            direction='row'
            alignItems='start'
            py={isMobile ? 0.5 : 1}
          >
            {/* {isForFeature ? (
              <QrCode2Icon
                sx={{
                  color: titleTextColor,
                  fontSize: isForFeature || !isMobile ? fontSize22 : fontSize18,
                }}
              />
            ) : ( */}
            <Box
              component='img'
              src='/static/images/img_star_bullet.png'
              // height='32px'
              // width='32px'
              height={typographyClamps.fontSize24}
              width={typographyClamps.fontSize24}
            />
            {/* )} */}
            <Typography
              sx={{
                pl: isMobile ? 1 : 2,
                color: titleTextColor ?? AppColors.whiteColor,
                fontSize:
                  isForFeature || !isMobile
                    ? typographyClamps.fontSize20
                    : typographyClamps.fontSize18,
                // lineHeight: 1.2,
                '& span': {
                  // background: AppColors.aiGradient,
                  // WebkitBackgroundClip: 'text',
                  // color: 'transparent',
                  fontWeight: 'bold',
                },
              }}
            >
              <HtmlRenderer htmlString={item} />
            </Typography>
          </Stack>
        ))}
      </Box>
    );
  };

  return (
    <Box
      // py={isForFeature ? 0 : 3}
      // my={3}
      sx={{
        background: isImageOnRight
          ? AppColors.aiGradientCrossTransparent
          : null,
      }}
    >
      <Grid
        container
        gap={{ sx: 0, sm: 5, md: 5 }}
        direction='row'
        justifyContent='space-between'
        alignItems={'center'}
        py={3}
        paddingLeft={{ xs: 2, sm: '5%', md: '8%', lg: '15%' }}
        paddingRight={
          // isForFeature ? 0 :
          { xs: 2, sm: '5%', md: '8%', lg: '15%' }
        }
        sx={{
          background: transparentBackground ? undefined : AppColors.aiGradient,
        }}
      >
        {/* {!isMobile && !isImageOnRight && ( */}
        {!(isForFeature && isMobile) && !isImageOnRight && (
          <Grid size={gridSize}>{getScreenshotView()}</Grid>
        )}

        <Grid size={gridSize}>
          <Box
            // width={isForFeature ? { xs: '100%', sm: '60%', md: '60%' } : '100%'}
            width={'100%'}
            // paddingLeft={isMobile ? 2 : '10%'}
            // paddingRight={isMobile ? 2 : '10%'}
            // py={isForFeature ? 3 : 0}
            display='flex'
            flexDirection='column'
            justifyContent='center'
            alignItems={{ xs: 'center', sm: 'center', md: 'start' }}
          >
            <Typography
              component='p'
              sx={{
                // fontSize: 'clamp(1.25rem, 2vw, 2rem)',
                fontSize: {
                  xs: typographyClamps.caption,
                  sm: typographyClamps.h6,
                  md: typographyClamps.h5,
                },
                fontWeight: 600,
                color: AppColors.whiteColor,
                textAlign: isMobile ? 'center' : 'start',
                // my: 1,
                '& span': {
                  background: AppColors.textGradient,
                  WebkitBackgroundClip: 'text',
                  color: 'transparent',
                },
              }}
            >
              {`Step ${idx + 1}`}
            </Typography>

            {title && (
              <Typography
                component='p'
                sx={{
                  // fontSize: 'clamp(1.25rem, 2vw, 2rem)',
                  fontSize: {
                    xs: typographyClamps.caption,
                    sm: typographyClamps.h6,
                    md: typographyClamps.h5,
                  },
                  fontWeight: 'bold',
                  color: titleTextColor ?? AppColors.whiteColor,
                  textAlign: isMobile ? 'center' : 'start',
                  width: '100%',
                  my: 1,
                  '& span': {
                    background: AppColors.textGradient,
                    WebkitBackgroundClip: 'text',
                    color: 'transparent',
                  },
                }}
              >
                {title}
                {/* {isForFeature ? (
                  title
                ) : (
                  <>
                    {`Step ${idx + 1}: `} <span>{title}</span>
                  </>
                )} */}
              </Typography>
            )}

            {subtitle && (
              <Typography
                component='p'
                textAlign={{ xs: 'center', sm: 'center', md: 'start' }}
                fontWeight={400}
                py={1}
                color='#6B727A'
              >
                <HtmlRenderer htmlString={subtitle} />
              </Typography>
            )}

            {isForFeature && isMobile && (
              <Box py={2}>{getScreenshotView()}</Box>
            )}

            {features && getFeatureListView(features)}

            {footerText && (
              <Typography
                sx={{ py: 1, color: AppColors.redColor, fontStyle: 'italic' }}
              >
                {footerText}
              </Typography>
            )}

            {/* <GradientButton sx={{ mt: 2 }} onClick={() => {}}>
            {isForFeature ? 'Learn More' : 'Get Started'}
          </GradientButton> */}
          </Box>
        </Grid>

        {/* {!isMobile && isImageOnRight && ( */}
        {!(isForFeature && isMobile) && isImageOnRight && (
          <Grid size={gridSize}>{getScreenshotView()}</Grid>
        )}
      </Grid>
    </Box>
  );
};
